import fs from "node:fs"
import path from "node:path"
import { fileURLToPath } from "node:url"
import { execa as execaFn } from "execa"
import type { Infra } from "./toEcosystem"
import type { Url } from "./utils" // getFreePort removed
import { getPackageRoot } from "./utils"

export type MeeNodeParameters = {
  infras: Infra[]
}

export type MeeNode = Awaited<ReturnType<typeof toMeeNode>>

export const toMeeNode = async (parameters: MeeNodeParameters) => {
  const { infras } = parameters

  const hostPort = 3000
  const host = "localhost"
  const url: Url = `http://${host}:${hostPort}`

  console.log(
    `MEE Node using package docker-compose.yml on port: ${hostPort}`
  )

  const packageRoot = getPackageRoot()
  const dockerComposeFilePath = path.join(packageRoot, "docker-compose.yml")

  if (!fs.existsSync(dockerComposeFilePath)) {
    throw new Error(
      `Package docker-compose.yml not found at ${dockerComposeFilePath}.`
    )
  }

  const projectName = `meenode-project-root-instance`

  // --- Paths to bundled assets within THIS @biconomy/ecosystem package ---
  // Assumes this file (toMeeNode.js after compilation) is in <package_root>/dist/
  const packageDistDir = path.dirname(fileURLToPath(import.meta.url))

  const bundledDuplicateScriptPath = path.resolve(
    packageDistDir,
    "scripts",
    "duplicate-chain.js"
  )
  const bundledTemplatesDir = path.resolve(
    packageDistDir,
    "templates",
    "chains-testnet"
  )
  // --- End of paths for bundled assets ---

  // Output directory for generated chain configs in the user's project
  const userChainsTestnetDir = path.join(
    packageRoot,
    "chains-testnet"
  )

  console.log(
    `Attempting to stop/remove any existing MEE Node services for project ${projectName} using root docker-compose.yml...`
  )
  const initialDownArgs = [
    "-f",
    dockerComposeFilePath,
    "-p",
    projectName,
    "down",
    "-v"
  ]
  const spawnEnv = Object.entries(process.env)
    .filter(([, value]) => value !== undefined)
    .reduce(
      (obj, [key, value]) => {
        obj[key] = value!
        return obj
      },
      {} as Record<string, string>
    )
  try {
    const initialDownResult = await execaFn(
      "docker",
      ["compose", ...initialDownArgs],
      {
        cwd: packageRoot,
        stdio: ["inherit", "inherit", "pipe"],
        env: spawnEnv
      }
    )
    if (initialDownResult.stderr && initialDownResult.exitCode === 0) {
      console.log(
        `Initial 'docker compose down' stderr (on success): ${initialDownResult.stderr}`
      )
    } else if (initialDownResult.exitCode !== 0) {
      console.warn(
        `Initial 'docker compose down' for ${projectName} exited with code ${initialDownResult.exitCode}. Stderr: ${initialDownResult.stderr}`
      )
    }
  } catch (error: any) {
    console.warn(
      `Warning during initial 'docker compose down' for ${projectName}: ${error.message}`
    )
    if (error.stderr)
      console.warn(
        `Initial 'docker compose down' stderr (on error): ${error.stderr}`
      )
  }

  const essentialUserFiles = ["84532.json", "1.json"]
  console.log(
    `Cleaning up user chain configurations in ${userChainsTestnetDir}, preserving ${essentialUserFiles.join(", ")}...`
  )
  try {
    if (!fs.existsSync(userChainsTestnetDir)) {
      await fs.promises.mkdir(userChainsTestnetDir, { recursive: true })
      console.log(`Created user directory ${userChainsTestnetDir}`)
    } else {
      const files = await fs.promises.readdir(userChainsTestnetDir)
      for (const file of files) {
        if (file.endsWith(".json") && !essentialUserFiles.includes(file)) {
          await fs.promises.unlink(path.join(userChainsTestnetDir, file))
        }
      }
    }
  } catch (err) {
    console.warn(
      `Warning: Could not clean/ensure user directory ${userChainsTestnetDir}:`,
      err
    )
  }

  if (!fs.existsSync(bundledDuplicateScriptPath)) {
    throw new Error(
      `Packaged script duplicate-chain.js not found at ${bundledDuplicateScriptPath}. Ensure @biconomy/ecosystem is built correctly and script is in dist/scripts/.`
    )
  }
  if (
    !fs.existsSync(bundledTemplatesDir) ||
    !fs.existsSync(path.join(bundledTemplatesDir, "84532.json"))
  ) {
    throw new Error(
      `Packaged template directory or 84532.json not found at ${bundledTemplatesDir}. Ensure @biconomy/ecosystem is built correctly and templates are in dist/templates/chains-testnet/.`
    )
  }

  console.log(
    `Generating chain configuration files using script: ${bundledDuplicateScriptPath}`
  )
  for (const infra of infras) {
    const chainId = infra.network.chainId
    try {
      const result = await execaFn(
        "node",
        [
          bundledDuplicateScriptPath,
          `--chainId=${chainId}`,
          `--templateDir=${bundledTemplatesDir}`,
          `--outputDir=${userChainsTestnetDir}`
        ],
        {
          stdio: ["pipe", "pipe", "pipe"]
        }
      )
      if (result.stderr)
        console.warn(
          `duplicate-chain.js for chainId ${chainId} stderr:`,
          result.stderr
        )
    } catch (error: any) {
      console.error(
        `Error running duplicate-chain.js for chainId ${chainId}: ${error.message}`
      )
      if (error.stderr)
        console.error(`duplicate-chain.js critical stderr: ${error.stderr}`)
      throw new Error(
        `Failed to execute duplicate-chain.js for chainId ${chainId}. Error: ${error.message}`
      )
    }
  }

  const composeUpArgs = [
    "-f",
    dockerComposeFilePath,
    "-p",
    projectName,
    "up",
    "--remove-orphans",
    "-d"
  ]

  console.log(
    `Starting MEE Node with project ${projectName} on port ${hostPort} using root docker-compose.yml... Cmd: docker compose ${composeUpArgs.join(" ")}`
  )
  try {
    await execaFn("docker", ["compose", ...composeUpArgs], {
      cwd: packageRoot,
      stdio: ["inherit", "inherit", "pipe"],
      env: spawnEnv
    })
    console.log(
      `MEE Node (project ${projectName}) services starting command dispatched.`
    )

    const readinessUrl = `${url}/v3/info`
    const maxRetries = 30
    let retries = 0
    let meeNodeReady = false
    console.log(`Waiting for MEE Node to be ready at ${readinessUrl}...`)
    while (retries < maxRetries) {
      try {
        const response = await fetch(readinessUrl)
        if (response.ok) {
          meeNodeReady = true
          console.log("MEE Node is ready.")
          break
        }
      } catch (e: any) {
        /* Ignore and retry */
      }
      retries++
      await new Promise((resolve) => setTimeout(resolve, 2000))
    }

    if (!meeNodeReady) {
      console.error(
        `MEE Node at ${readinessUrl} did not become ready after ${maxRetries * 2} seconds.`
      )
      try {
        console.error("Fetching MEE Node logs for diagnostics...")
        await execaFn(
          "docker",
          [
            "compose",
            "-f",
            dockerComposeFilePath,
            "-p",
            projectName,
            "logs",
            "--tail=100",
            "node"
          ],
          {
            cwd: packageRoot,
            stdio: "inherit"
          }
        )
      } catch (logError) {
        console.error("Failed to fetch MEE Node logs.", logError)
      }
      throw new Error(`MEE Node at ${readinessUrl} did not become ready.`)
    }
  } catch (error: any) {
    const stderrContent = error.stderr || "No stderr output captured."
    const errorMessage = `docker compose up command for project ${projectName} failed (exit code ${error.exitCode || "unknown"}). Stderr: ${stderrContent}`
    console.error(errorMessage)
    throw new Error(errorMessage)
  }

  console.log(`MEE Node (project ${projectName}) running on ${url}.`)
  console.log(
    `Logs: docker compose -f ${dockerComposeFilePath} -p ${projectName} logs -f --tail=50`
  )

  return {
    chainIds: infras.map((infra) => infra.network.chainId),
    url,
    projectName,
    meeDeploymentDir: packageRoot,
    async stop() {
      console.log(
        `Stopping MEE Node docker compose project ${projectName} using root docker-compose.yml...`
      )
      const downArgs = [
        "-f",
        dockerComposeFilePath,
        "-p",
        projectName,
        "down",
        "-v"
      ]
      try {
        const downResult = await execaFn("docker", ["compose", ...downArgs], {
          cwd: packageRoot,
          stdio: ["inherit", "inherit", "pipe"],
          env: spawnEnv
        })
        if (downResult.stderr && downResult.exitCode === 0) {
          console.warn(
            `docker compose down for ${projectName} stderr (on success): ${downResult.stderr}`
          )
        }
        console.log(`MEE Node project ${projectName} stopped successfully.`)
      } catch (error: any) {
        const stderrContent = error.stderr || "No stderr output captured."
        console.error(
          `Error stopping ${projectName} (exit code ${error.exitCode || "unknown"}). Stderr: ${stderrContent}`
        )
      }
    }
  }
}
